import React, { useEffect, useMemo } from 'react'
import { useSession } from 'src/sdk/session'
import { useSearch } from '@faststore/sdk'
import { useProductsQuery } from 'src/sdk/product/useProductsQuery'
import { useFilterContext } from 'src/contexts/FilterContext/filter-context'
import { useInView } from 'react-intersection-observer'
import { useProductShelfViewItemListEvent } from 'src/sdk/analytics/hooks/useProductShelfViewItemListEvent'
import { getCookie } from 'src/utils/cookie'
import { getCategorie, getItemSizeFromProperties, getVariantWithSmallestSize } from 'src/utils/getProductDetailsInfo'
import Sentinel from 'src/sdk/search/Sentinel'
import ProductGrid from 'src/components/product/ProductGrid'
import type {
  IStoreSelectedFacet,
  ProductsQueryQuery,
} from '@generated/graphql'

import ProductTiles from '../ProductTiles'

/* If showSponsoredProducts is true, a ProductTiles will be displayed in between two blocks of ProductGrid on the page 0 */
interface Props {
  page: number
  display?: boolean
  fallbackData?: ProductsQueryQuery
  title: string
  showSponsoredProducts?: boolean
  isLoading?: boolean
  layoutGridOrList?: boolean
  setIsButtonLoading?: React.Dispatch<React.SetStateAction<boolean>>
  selectedFacets: IStoreSelectedFacet[]
}

function GalleryPage({
  page,
  display,
  title,
  fallbackData,
  showSponsoredProducts = true,
  isLoading = false,
  layoutGridOrList,
  setIsButtonLoading,
  selectedFacets: originalSelectedFacets,
}: Props) {
  const {
    itemsPerPage,
    state: { sort: sortSearch, term, selectedFacets },
  } = useSearch()

  const sort = useFilterContext()?.sort ?? sortSearch
  const productList = useProductsQuery(
    {
      first: itemsPerPage,
      after: (itemsPerPage * page).toString(),
      sort,
      term: term ?? '',
      selectedFacets: originalSelectedFacets ?? selectedFacets,
    },
    {
      fallbackData,
      revalidateOnMount: fallbackData == null,
    }
  )

  const products = useMemo(
    () => productList?.edges?.map((edge) => edge.node),
    [productList]
  )

  const { sendProductShelfViewItemList } = useProductShelfViewItemListEvent()
  const { currency: { code }, person } = useSession()
  const uuidToUse = getCookie('uuid')

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.10,
  })

  const productsSponsored = products?.slice(0, 2)

  const middleItemIndex = Math.ceil(itemsPerPage / 2)

  if (display === false || products == null) {
    return null
  }

  const shouldDisplaySponsoredProducts =
    showSponsoredProducts &&
    page === 0 &&
    productsSponsored !== undefined &&
    productsSponsored.length > 1

  useEffect(() => {
    if (!inView || !products?.length) {
      return
    }

    const productItems = products.map((product) => {
      const productToUse = getVariantWithSmallestSize(product)
   
      const skuId = productToUse?.sku

      const productIdMatch = product?.slug?.match(/\b[a-z]{2}\d{8}\b/)

      const productId = productIdMatch ? productIdMatch[0].toUpperCase() : ""

      const productName = product?.name

      const productPrice = productToUse?.offers?.offers?.[0]?.price

      const productDiscount = (
        (product?.offers?.offers?.[0]?.listPrice - product?.offers?.offers?.[0]?.price) * 1
      )

      const productType = product?.name?.match(/^\p{L}+/u)?.[0] ?? ""

      const productWeight = product?.name?.match(/(\d+)\s*k/i)?.[0] ?? ""

      const productBrand = product?.brand?.brandName

      const productReference = productToUse?.gtin

      const categories = getCategorie(
        product?.breadcrumbList?.itemListElement.map((category) => category),
        productName
      )

      const productCollection =
        String(
          product?.isVariantOf?.additionalProperty.find(
            (property) => property.name.toLowerCase() === 'coleção'
          )?.value ?? ''
        ) || ''

      const productMaterial =
        String(
          product?.isVariantOf?.additionalProperty.find(
            (property) => property.name.toLowerCase() === 'material'
          )?.value ?? ''
        ) || ''

      const productSize = getItemSizeFromProperties(
        product?.isVariantOf?.additionalProperty,
        productToUse?.additionalProperty
      )

      const productStone =
        String(
          product?.isVariantOf?.additionalProperty.find(
            (property) =>
              property.name.toLowerCase() === 'pedras'.toLowerCase()
          )?.value ?? ''
        ) || ''

      const productAudience =
        String(
          product?.isVariantOf?.additionalProperty.find(
            (property) =>
              property.name.toLowerCase() === 'sugestão'.toLowerCase()
          )?.value ?? ''
        ) || ''

      return {
        sku_id: skuId,
        item_id: productId,
        item_name: productName,
        item_price: productPrice,
        item_type: productType,
        item_weight: productWeight,
        item_brand: productBrand,
        item_collection: productCollection,
        item_reference: productReference,
        item_material: productMaterial,
        item_category: categories[0] ?? "",
        item_category2: categories[1] ?? "",
        item_category3: categories[2] ?? "",
        item_category4: categories[3] ?? "",
        item_category5: categories[4] ?? "",
        item_category6: categories[5] ?? "",
        item_currency: code,
        item_size: productSize ?? "",
        item_stone: productStone,
        item_audience: productAudience,
        quantity: 1,
        discount: productDiscount
      }
    })

    const eventData = {
      items: productItems,
      user_logged: !!person?.id,
      user_id: person?.id ?? null,
      uuid: uuidToUse
    }

    sendProductShelfViewItemList(eventData)
  }, [inView, products])

  return (
    <div ref={ref}>
      <Sentinel
        products={products}
        page={page}
        pageSize={itemsPerPage}
        title={title}
      />
      {shouldDisplaySponsoredProducts ? (
        <>
          <ProductGrid
            products={products?.slice(0, middleItemIndex)}
            page={page}
            pageSize={middleItemIndex}
            isLoading={isLoading}
            layoutGridOrList={layoutGridOrList}
            setIsButtonLoading={setIsButtonLoading}
            sort={sort}
          />
          <div className="product-listing__results-sponsored">
            <h3>Sponsored</h3>
            <ProductTiles products={productsSponsored?.slice(0, 2)} />
          </div>
          <ProductGrid
            products={products?.slice(middleItemIndex, itemsPerPage)}
            page={page}
            pageSize={middleItemIndex}
            isLoading={isLoading}
            layoutGridOrList={layoutGridOrList}
            setIsButtonLoading={setIsButtonLoading}
            sort={sort}
          />
        </>
      ) : (
        <ProductGrid
          products={products}
          page={page}
          pageSize={itemsPerPage}
          isLoading={isLoading}
          layoutGridOrList={layoutGridOrList}
          setIsButtonLoading={setIsButtonLoading}
          sort={sort}
        />
      )}
    </div>
  )
}

export default GalleryPage